import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarClock,
  ClipboardList,
  Link2,
  AlertCircle,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react'
import { ordersApi } from '@/services/ordersApi'
import { getPendingBotDoubts, type PendingBotDoubt } from '@/services/dashboardApi'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import { useChannelInstances } from '@/hooks/useChannelInstances'
import type { ChannelInstanceResponse } from '@/services/channelsApi'
import { dashboardCopy } from './dashboardCopy'

export type AttentionSeverity = 'channel' | 'consultation' | 'transaction' | 'configuration'

export interface AttentionItem {
  id: string
  severity: AttentionSeverity
  label: string
  count?: number
  to: string
  actionLabel: string
}

/** Filas de "Requiere atención". Ordenadas por urgencia: canal, consultas, pedidos/citas, configuración. */
export function buildAttentionItems({
  instances,
  hasAnyChannel,
  pendingDoubts,
  pendingAppointments,
  ordersSummary,
  profile,
}: {
  instances: ChannelInstanceResponse[]
  hasAnyChannel: boolean
  pendingDoubts: PendingBotDoubt[]
  pendingAppointments?: number
  ordersSummary?: { pending: { count: number } }
  profile: { operational: { hasFulfillment: boolean; hasAppointmentsCalendar: boolean }; nav: { primaryTransactionPath: string } }
}): AttentionItem[] {
  const items: AttentionItem[] = []

  const disconnected = instances.filter(
    (i) => i.status === 'DISCONNECTED' || i.status === 'QRCODE'
  )
  if (!hasAnyChannel || disconnected.length > 0) {
    items.push({
      id: 'channel',
      severity: 'channel',
      label: hasAnyChannel ? dashboardCopy.disconnectedChannel : dashboardCopy.noChannels,
      count: disconnected.length || undefined,
      to: '/app/instances?create=true',
      actionLabel: dashboardCopy.connectChannel,
    })
  }

  const doubtsCount = pendingDoubts.length
  if (doubtsCount > 0) {
    items.push({
      id: 'pending-doubts',
      severity: 'consultation',
      label: dashboardCopy.pendingDoubtsRow(doubtsCount),
      count: doubtsCount,
      to: '/app/conversations',
      actionLabel: dashboardCopy.reviewInbox,
    })
  }

  if (profile.operational.hasFulfillment && (ordersSummary?.pending.count ?? 0) > 0) {
    const count = ordersSummary!.pending.count
    items.push({
      id: 'pending-orders',
      severity: 'transaction',
      label: dashboardCopy.pendingOrdersRow(count),
      count,
      to: '/app/orders',
      actionLabel: dashboardCopy.reviewOrders,
    })
  }

  if (profile.operational.hasAppointmentsCalendar) {
    const count = pendingAppointments ?? 0
    if (count > 0) {
      items.push({
        id: 'pending-appointments',
        severity: 'transaction',
        label: dashboardCopy.pendingAppointmentsRow(count),
        count,
        to: profile.nav.primaryTransactionPath,
        actionLabel: dashboardCopy.reviewAppointments,
      })
    }
  }

  const severityOrder: AttentionSeverity[] = ['channel', 'consultation', 'transaction', 'configuration']
  return items.sort((a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity))
}

interface AttentionCenterProps {
  pendingAppointments?: number
  hasAnyChannel: boolean
}

/**
 * Centro de atención: lo accionable primero, en una sola tarjeta.
 * Usa las mismas queryKey que el resto del dashboard (sin peticiones nuevas).
 */
export function AttentionCenter({ pendingAppointments, hasAnyChannel }: AttentionCenterProps) {
  const navigate = useNavigate()
  const tenantId = useAuthStore((s) => s.tenantId)
  const { profile } = useVerticalUI()

  const { data: instances = [] } = useChannelInstances()

  const { data: pendingDoubts = [] } = useQuery({
    queryKey: queryKeys.pendingBotDoubts(tenantId),
    queryFn: getPendingBotDoubts,
    enabled: Boolean(tenantId),
    // Sin sondeo propio: `/dashboard/stats` sigue siendo la única consulta periódica.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  })

  const { data: ordersSummary } = useQuery({
    queryKey: queryKeys.ordersSummary(tenantId),
    queryFn: () => ordersApi.getSummary(),
    enabled: Boolean(tenantId) && profile.operational.hasFulfillment,
    staleTime: 30_000,
  })

  const items = buildAttentionItems({
    instances,
    hasAnyChannel,
    pendingDoubts,
    pendingAppointments,
    ordersSummary,
    profile,
  })

  return (
    <section aria-labelledby="attention-center-title" className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 id="attention-center-title" className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <ShieldCheck className="h-5 w-5 text-brand" aria-hidden="true" />
          {dashboardCopy.attentionTitle}
        </h2>
        <span aria-hidden="true" className="text-xs font-semibold text-slate-500">
          {items.length > 0 ? `${items.length}` : ''}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600" aria-hidden="true">✓</span>
          <span><span className="font-semibold text-slate-800">{dashboardCopy.allCaughtUp}.</span> {dashboardCopy.allCaughtUpHint}</span>
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => navigate(item.to)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <AttentionIcon item={item} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">{item.label}</span>
                    {item.count ? (
                      <span className="text-xs font-medium text-slate-500">{item.count}</span>
                    ) : null}
                  </span>
                </span>
                <span className="flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-brand">
                  {item.actionLabel}
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function AttentionIcon({ item }: { item: AttentionItem }) {
  const className = 'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl'
  switch (item.severity) {
    case 'channel':
      return (
        <span className={`${className} bg-rose-50 text-rose-600`} aria-hidden="true">
          <Link2 className="h-5 w-5" />
        </span>
      )
    case 'consultation':
      return (
        <span className={`${className} bg-amber-50 text-amber-600`} aria-hidden="true">
          <AlertCircle className="h-5 w-5" />
        </span>
      )
    case 'transaction':
      return (
        <span className={`${className} bg-sky-50 text-sky-600`} aria-hidden="true">
          {item.id === 'pending-orders' ? <ClipboardList className="h-5 w-5" /> : <CalendarClock className="h-5 w-5" />}
        </span>
      )
    default:
      return (
        <span className={`${className} bg-slate-100 text-slate-500`} aria-hidden="true">
          <ClipboardList className="h-5 w-5" />
        </span>
      )
  }
}

export default AttentionCenter
